import {Matrix4, Vector3} from '@math.gl/core';
import {WORLD_SIZE} from './constants';
import {EventEmitter} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {groupBy} from '../utils';

export class BaseLayerDeck {
    id;
    mapContext;
    layers = {};
    subscription = new Subscription();
    visible = false;
    onViewportLoad = new EventEmitter();
    onHover = new EventEmitter();

    get allLayers() {
        return this.layerIds.map(id => this.layers[id]);
    }

    get layerIds() {
        return Object.keys(this.layers);
    }

    constructor(id) {
        this.id = id;
    }


    init(map, visible = true, underLayerId = null, loadedCallback: (layer) => void = null) {
        this.mapContext = map;
        this.subscription = new Subscription();
        this.visible = visible;
        let layers = [...this.mapContext.deckInstance.props.layers];
        if (underLayerId) {
            const id = this.layerIds[0];
            this.layers[id] = this.layers[id].clone({underLayerId});
        }
        this.layerIds.forEach(
            id => {
                const _data = {visible};
                let loaded = false;
                if (loadedCallback) {
                    _data['onViewportLoad'] = (_) => {
                        if (!loaded) {
                            loadedCallback(this);
                            loaded = true;
                        }
                    };
                } else {
                    _data['onViewportLoad'] = (_) => {
                        this.onViewportLoad.emit(this);
                    };
                }
                this.layers[id] = this.layers[id].clone(_data);
            }
        );
        this.allLayers.forEach(
            layer => layers = this.addInTheRightPosition(layer, layers)
        );
        this.mapContext.deckInstance.setProps({layers});
    }

    destroy() {
        this.subscription.unsubscribe();
        const layers = [...this.mapContext.deckInstance.props.layers];
        this.layerIds.forEach(layerId => {
            const layerPosition = layers.findIndex(l => l.id === layerId);
            if (layerPosition === -1) {
                console.error(`The layer ${layerId} cannot be removed, it isn't loaded.`);
            } else {
                layers.splice(layerPosition, 1);
            }
        });
        this.mapContext.deckInstance.setProps({layers});
    }

    show() {
        if (!this.visible) {
            this.updateAll({visible: true});
            this.visible = true;
            this.readViewPortFeatures();
        }
    }

    hide() {
        if (this.visible) {
            this.updateAll({visible: false});
            this.visible = false;
        }
    }

    updateAll(data) {
        if (!this.mapContext || !this.mapContext.deckInstance) {
            return;
        }

        if (!data) {
            console.error('You need to pass data in order to update layers.');
        }

        const layers = [...this.mapContext.deckInstance.props.layers];

        this.layerIds.forEach(layerId => {
            const layerPosition = layers.findIndex(l => l.id === layerId);
            if (layerPosition === -1) {
                console.error(`You must init layer ${layerId} before update it.`);
            } else {
                layers[layerPosition] = this.layers[layerId] = this.layers[layerId].clone(data);
            }
        });

        this.mapContext.deckInstance.setProps({layers});
    }

    update(id, data) {
        if (!this.mapContext || !this.mapContext.deckInstance) {
            return;
        }
        const layer = this.layers[id];
        if (!layer) {
            console.error('This layer doesn\'t exist.');
        }

        const layers = [...this.mapContext.deckInstance.props.layers];
        const layerPosition = layers.findIndex(l => l.id === id);
        if (layerPosition === -1) {
            console.error('You must init layers before update them.');
        }

        layers.splice(layerPosition, 1);
        let layerCopy;
        layerCopy = layer.clone(data);

        this.layers[id] = layerCopy;
        this.mapContext.deckInstance.setProps({layers: this.addInTheRightPosition(layerCopy, layers)});
        return layerCopy;
    }

    updateFilters(city) {
    }

    hover(hover = false) {
        this.onHover.emit(hover);
    }

    protected readViewPortFeatures() {
    }

    private addInTheRightPosition(layer, layers) {
        const {overLayerId, underLayerId} = layer['props'];
        const idx = layers.findIndex(l => l.id === layer.id);
        if (idx > -1) {
            layers[idx] = layer;
            return layers;
        }

        if (overLayerId && underLayerId) {
            console.error('Cannot use overLayerId and underLayerId at the same time');
        }

        const baseLayerId = overLayerId || underLayerId;

        if (baseLayerId) {
            const layerIdx = layers.findIndex(l => l.id === baseLayerId);

            const baseLayerFound = layerIdx !== -1;

            if (baseLayerFound) {
                if (overLayerId) {
                    layers.splice(layerIdx + 1, 0, layer);
                } else if (underLayerId) {
                    layers.splice(layerIdx, 0, layers);
                }
            } else {
                layers.push(layer);
            }
        } else {
            layers.push(layer);
        }

        return layers;
    }

    protected openPopup(properties, popup, coordinates) {
        this.mapContext.loadPopup({
            template: popup,
            properties: properties,
            coordinates: coordinates
        });
    }

    private getTransformationMatrixFromTile({x, y, z}) {
        const worldScale = 2 ** z;
        const xScale = WORLD_SIZE / worldScale;
        const yScale = -xScale;
        const xOffset = (WORLD_SIZE * x) / worldScale;
        const yOffset = WORLD_SIZE * (1 - y / worldScale);
        return new Matrix4()
            .translate([xOffset, yOffset, 0])
            .scale([xScale, yScale, 1]);
    }

    private isPointInViewport(point, planes) {
        const position = new Vector3(point[0], point[1], 0);
        let outDir = null;
        for (const direction in planes) {
            if (planes[direction]) {
                const plane = planes[direction];
                if (position.dot(plane.normal) > plane.distance) {
                    outDir = direction;
                    break;
                }
            }
        }
        return outDir === null;
    }

    private isFeatureInViewport(feature, cache, matrix, planes) {
        if (!feature.properties) {
            return false;
        }
        // TODO: make this configurable
        const id = feature.properties.cartodb_id || feature.id;
        if (cache.has(id)) {
            return false; // avoid duplicate features across tiles
        }
        let isInside = false;
        const type = feature.geometry.type;
        if (type === 'Point') {
            const point = matrix.transform(feature.geometry.coordinates);
            isInside = this.isPointInViewport(point, planes);
        } else if (type === 'Polygon') {
            const rings = feature.geometry.coordinates;
            isInside = rings.some(polygon => polygon.some(point => this.isPointInViewport(
                matrix.transform(point),
                planes
            )));
        } else if (type === 'MultiPolygon') {
            const polygons = feature.geometry.coordinates;
            isInside = polygons.some(ring => ring.some(polygon => polygon.some(point => this.isPointInViewport(
                matrix.transform(point),
                planes
            ))));
        } else if (type === 'LineString') {
            const points = feature.geometry.coordinates;
            isInside = points.every(point => this.isPointInViewport(
                matrix.transform(point),
                planes
            ));
        } else if (type === 'MultiLineString') {
            isInside = feature.geometry.coordinates.some(points =>
                points.every(point => this.isPointInViewport(
                    matrix.transform(point),
                    planes
                ))
            );
        } else {
            // eslint-disable-next-line
            console.error('[layerService.js] Unsupported geometry type for viewportFeatues', feature.geometry.type);
        }
        if (isInside) {
            cache.add(id);
        }
        return isInside;
    }

    getViewportFeatures(subLayer = null) {
        if (!this.mapContext || !this.mapContext.deckInstance) {
            return [];
        }
        const layers_ids = subLayer ? [subLayer] : Object.keys(this.layers);
        const layers = this.mapContext.deckInstance.layerManager.layers.filter(l => layers_ids.includes(l.id));
        if (!layers) {
            return [];
        }
        const cache = new Set();
        const viewport = this.mapContext.deckInstance.getViewports()[0];
        const planes = viewport.getFrustumPlanes();
        let tiles: any = [];
        layers.forEach(layer => {
            tiles = tiles.concat(layer.state.tileset.selectedTiles);
        });
        return tiles.map(t => {
            const matrix = this.getTransformationMatrixFromTile(t);
            const features = t.content || [];
            return features.filter(
                (f, index, self) => this.isFeatureInViewport(f, cache, matrix, planes)
                    && self.map(s => s.properties.cartodb_id).indexOf(f.properties.cartodb_id) === index
            );
        }).flat();
    }

    getGeoJsonViewportFeatures({ features }) {
        if (!this.mapContext || !this.mapContext.deckInstance) {
            return [];
        }
    
        const viewport = this.mapContext._bbox;
    
        return features.filter((feature) => {
            const geometry = feature.geometry;
            let isInViewport = false;
    
            switch (geometry.type) {
                case 'Point':
                    const [x, y] = geometry.coordinates;
                    isInViewport = (
                        viewport[0] <= x &&
                        viewport[1] <= y &&
                        viewport[2] >= x &&
                        viewport[3] >= y
                    );
                    break;
    
                case 'LineString':
                    isInViewport = geometry.coordinates.some(([x, y]) => (
                        viewport[0] <= x &&
                        viewport[1] <= y &&
                        viewport[2] >= x &&
                        viewport[3] >= y
                    ));
                    break;
    
                case 'Polygon':
                    isInViewport = geometry.coordinates[0].some(([x, y]) => (
                        viewport[0] <= x &&
                        viewport[1] <= y &&
                        viewport[2] >= x &&
                        viewport[3] >= y
                    ));
                    break;
    
                default:
                    break;
            }
    
            return isInViewport;
        });
    }

    countGeoJsonViewportFeatures({ features, city, layerId, allowed = [], variable = 'types', groupByLayer = 'layer'}: any) {
        const viewportFeatures = this.getGeoJsonViewportFeatures({ features })
        
        const groupedByLayer = groupBy(
            viewportFeatures,
            groupByLayer
        );

        city[variable].forEach(v => {
            if (allowed.includes(v.idText)) {
                if (!v.showing) {
                    v.data = [];
                }
            }
        });
        
        if (Object.keys(groupedByLayer).length) {
            for (const group of Object.keys(groupedByLayer)) {
                const groupType = city[variable].find(v => v.idText === group && v.showing);
                if (groupType) {
                    groupType.data = groupedByLayer[group];
                }
            }
        }
    }

    updateCounters() {
        this.readViewPortFeatures();
    }
}



// WEBPACK FOOTER //
// ./src/app/common/layers/baseLayerDeck.ts